import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Select, Button, Space, Row, Col } from 'antd';
import { SearchOutlined, ClearOutlined } from '@ant-design/icons';
import { useTheme } from '@/components/common/ThemeProvider';
import { NubGroupDTO } from '../types';

const { Option } = Select;

export interface UserFilters {
    searchField?: string;
    searchValue?: string;
    primaryGroup?: string;
    institution?: string;
    enabled?: boolean;
}

interface NubUserFiltersProps {
    groups: NubGroupDTO[];
    onFiltersChange: (filters: UserFilters) => void;
    loading?: boolean;
}

export const NubUserFilters: React.FC<NubUserFiltersProps> = ({
    groups,
    onFiltersChange,
    loading = false
}) => {
    const { dark } = useTheme();
    const [form] = Form.useForm();
    const [filters, setFilters] = useState<UserFilters>({});
    const [selectedSearchField, setSelectedSearchField] = useState<string | undefined>();

    const searchFieldOptions = [
        { value: 'userId', label: 'User ID' },
        { value: 'email', label: 'Email' },
        { value: 'fullName', label: 'Full Name' },
        { value: 'primaryGroup', label: 'Primary Group' },
        { value: 'institution', label: 'Institution' }
    ];

    const handleSearch = () => {
        const formValues = form.getFieldsValue();
        const newFilters: UserFilters = {};

        // Handle general search
        if (formValues.searchField && formValues.searchValue) {
            newFilters.searchField = formValues.searchField;
            newFilters.searchValue = formValues.searchValue.trim();
        }

        // Handle specific filters
        if (formValues.primaryGroup) {
            newFilters.primaryGroup = formValues.primaryGroup;
        }

        if (formValues.institution) {
            newFilters.institution = formValues.institution;
        }

        if (formValues.enabled !== undefined) {
            newFilters.enabled = formValues.enabled;
        }

        setFilters(newFilters);
        onFiltersChange(newFilters);
    };

    const handleClear = () => {
        form.resetFields();
        setFilters({});
        setSelectedSearchField(undefined);
        onFiltersChange({});
    };

    const handleFieldChange = (value: string) => {
        setSelectedSearchField(value);
        // Clear search value when search field changes
        if (value) {
            form.setFieldsValue({ searchValue: '' });
        }
    };

    // Auto-search when filters change (debounced)
    useEffect(() => {
        const timer = setTimeout(() => {
            const formValues = form.getFieldsValue();
            if (formValues.searchValue && formValues.searchValue.trim().length >= 2) {
                handleSearch();
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [form.getFieldsValue()?.searchValue]);

    const primaryGroups = groups.filter(group => group.primary);

    return (
        <Card 
            className={`mb-4 ${dark ? 'bg-gray-800' : ''}`}
            title="Filters"
            size="small"
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSearch}
            >
                <Row gutter={16}>
                    <Col xs={24} sm={12} md={6}>
                        <Form.Item
                            name="searchField"
                            label="Search Field"
                        >
                            <Select
                                placeholder="Select field to search"
                                allowClear
                                onChange={handleFieldChange}
                                value={selectedSearchField}
                            >
                                {searchFieldOptions.map(option => (
                                    <Option key={option.value} value={option.value}>
                                        {option.label}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} md={6}>
                        <Form.Item
                            name="searchValue"
                            label="Search Value"
                        >
                            <Input
                                placeholder="Enter search value"
                                disabled={!selectedSearchField}
                                suffix={<SearchOutlined />}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} md={6}>
                        <Form.Item
                            name="primaryGroup"
                            label="Primary Group"
                        >
                            <Select
                                placeholder="Filter by primary group"
                                allowClear
                                showSearch
                                optionFilterProp="children"
                            >
                                {primaryGroups.map(group => (
                                    <Option key={group.code} value={group.code}>
                                        {group.name} ({group.code})
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} md={6}>
                        <Form.Item
                            name="enabled"
                            label="Status"
                        >
                            <Select
                                placeholder="Filter by status"
                                allowClear
                            >
                                <Option value={true}>Enabled</Option>
                                <Option value={false}>Disabled</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Row>
                    <Col span={24}>
                        <Space>
                            <Button
                                type="primary"
                                icon={<SearchOutlined />}
                                onClick={handleSearch}
                                loading={loading}
                            >
                                Search
                            </Button>
                            <Button
                                icon={<ClearOutlined />}
                                onClick={handleClear}
                            >
                                Clear
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </Form>
        </Card>
    );
};
